// Radial Memory Web layout (docs/05 §3). Angles in degrees, screen space (0 = east, 90 = south).
// Branches: FORTUNE north (270), GREED north-east (315), HANDS south-east (45),
// APPETITE south (90), RITUALS south-west (135), CONTRACTS north-west (225).
// Each entry lists its edges to already-placed nodes (buyable when any neighbour is owned).

export interface WebPlacement {
  id: string;
  angle: number;
  r: number;
  links: string[];
}

export const WEB_UNIT = 48;

export const WEB_LAYOUT: WebPlacement[] = [
  { id: 'ROOT', angle: 0, r: 0, links: [] },

  // Gates: the inner ring and the path to It Wakes.
  { id: 'G_PAIR', angle: 294, r: 1.8, links: ['ROOT'] },
  { id: 'G_HOLD', angle: 25, r: 1.8, links: ['ROOT'] },
  { id: 'G_DRAFT', angle: 165, r: 1.8, links: ['ROOT'] },
  { id: 'G_NUDGE', angle: 62, r: 3.0, links: ['G_HOLD'] },
  { id: 'G_CHARMS', angle: 132, r: 3.0, links: ['G_DRAFT'] },
  { id: 'G_BONUS_SCRATCH', angle: 150, r: 4.4, links: ['G_CHARMS'] },
  { id: 'G_GROW_1', angle: 97, r: 4.2, links: ['G_NUDGE', 'G_CHARMS'] },
  { id: 'G_COLUMNS', angle: 97, r: 5.5, links: ['G_GROW_1'] },
  { id: 'G_DIAGONALS', angle: 97, r: 6.8, links: ['G_COLUMNS'] },
  { id: 'G_FEED', angle: 110, r: 5.4, links: ['G_GROW_1'] },
  { id: 'G_ROT', angle: 114, r: 6.7, links: ['G_FEED'] },

  // Act II gates: deals and bonuses (south-west), growth and The Hand (south).
  { id: 'G_PROPOSITIONS', angle: 124, r: 5.5, links: ['G_GROW_1'] },
  { id: 'G_BONUS_HILO', angle: 132, r: 6.7, links: ['G_PROPOSITIONS'] },
  { id: 'G_BONUS_WHEEL', angle: 138, r: 7.9, links: ['G_BONUS_HILO'] },
  { id: 'G_HEX', angle: 122, r: 8.0, links: ['G_ROT'] },
  { id: 'G_GROW_2', angle: 100, r: 8.1, links: ['G_DIAGONALS'] },
  { id: 'G_GROW_3', angle: 94, r: 9.5, links: ['G_GROW_2'] },
  { id: 'G_VEE', angle: 86, r: 10.9, links: ['G_GROW_3'] },
  { id: 'G_ZIG', angle: 99, r: 10.9, links: ['G_GROW_3'] },
  { id: 'G_MOLT', angle: 92, r: 12.2, links: ['G_GROW_3', 'G_VEE', 'G_ZIG'] },

  // Act III–IV: the eyes open, the machine deepens, the table is set.
  { id: 'G_EYES', angle: 92, r: 13.6, links: ['G_MOLT'] },
  { id: 'G_EYE_PATTERN', angle: 87, r: 15.0, links: ['G_EYES'] },
  { id: 'G_CROSS', angle: 97, r: 15.0, links: ['G_EYES'] },
  { id: 'G_BONUS_PLINKO', angle: 81, r: 14.3, links: ['G_EYES'] },
  { id: 'G_GROW_4', angle: 91, r: 16.4, links: ['G_EYE_PATTERN'] },
  { id: 'G_BONUS_FEAST', angle: 91, r: 17.8, links: ['G_GROW_4'] },
  { id: 'G_GROW_5', angle: 91, r: 19.2, links: ['G_BONUS_FEAST'] },

  // The Hand
  { id: 'H_AUTOSPIN', angle: 104, r: 9.8, links: ['G_GROW_2'] },
  { id: 'H_SPEED', angle: 108, r: 11.1, links: ['H_AUTOSPIN'] },
  { id: 'H_AUTOPAY', angle: 116, r: 10.5, links: ['H_AUTOSPIN'] },
  { id: 'H_AUTOHOLD', angle: 103, r: 12.3, links: ['H_SPEED'] },
  { id: 'H_AUTOFEED', angle: 114, r: 11.9, links: ['H_AUTOPAY', 'H_SPEED'] },
  { id: 'H_AUTONUDGE', angle: 109, r: 13.3, links: ['H_AUTOHOLD', 'H_AUTOFEED'] },
  { id: 'H_AUTODRAFT', angle: 120, r: 12.9, links: ['H_AUTOFEED'] },
  { id: 'H_AUTOBONUS', angle: 116, r: 14.3, links: ['H_AUTODRAFT', 'H_AUTONUDGE'] },

  // RITUALS (south-west): bonus upgrades hanging off Scratch of Fate.
  { id: 'RI01', angle: 158, r: 5.6, links: ['G_BONUS_SCRATCH'] },
  { id: 'RI02', angle: 168, r: 6.5, links: ['RI01'] },
  { id: 'RI03', angle: 150, r: 6.8, links: ['RI01'] },
  { id: 'RI04', angle: 160, r: 7.7, links: ['RI02', 'RI03'] },
  { id: 'RI05', angle: 146, r: 8.6, links: ['RI03', 'G_BONUS_HILO'] },
  { id: 'RI12', angle: 168, r: 8.8, links: ['RI04'] },
  { id: 'RI06', angle: 155, r: 9.4, links: ['RI05'] },
  { id: 'RI07', angle: 139, r: 9.7, links: ['RI05', 'G_BONUS_WHEEL'] },
  { id: 'RI08', angle: 145, r: 10.8, links: ['RI07'] },
  { id: 'RI13', angle: 162, r: 10.3, links: ['RI12', 'RI06'] },
  { id: 'RI09', angle: 171, r: 10.1, links: ['RI12'] },
  { id: 'RI10', angle: 176, r: 11.3, links: ['RI09'] },
  { id: 'RI11', angle: 159, r: 11.6, links: ['RI13'] },

  // APPETITE (south): hangs off It Wakes, with the rot nodes past Spoilage.
  { id: 'AP01', angle: 84, r: 5.5, links: ['G_GROW_1'] },
  { id: 'AP11', angle: 75, r: 6.6, links: ['AP01'] },
  { id: 'AP02', angle: 85, r: 6.8, links: ['AP01', 'G_FEED'] },
  { id: 'AP04', angle: 74, r: 7.9, links: ['AP11'] },
  { id: 'AP03', angle: 87, r: 8.1, links: ['AP02'] },
  { id: 'AP06', angle: 106, r: 8.0, links: ['G_ROT'] },
  { id: 'AP07', angle: 112, r: 9.2, links: ['AP06'] },
  { id: 'AP12', angle: 68, r: 9.1, links: ['AP04'] },
  { id: 'AP05', angle: 80, r: 9.2, links: ['AP04', 'AP03'] },
  { id: 'AP13', angle: 82, r: 10.4, links: ['AP05'] },
  { id: 'AP08_LAST_MEAL', angle: 71, r: 10.3, links: ['AP12', 'AP05'] },
  { id: 'AP09', angle: 76, r: 11.6, links: ['AP08_LAST_MEAL', 'AP13'] },
  { id: 'AP10', angle: 72, r: 12.8, links: ['AP09'] },

  // FORTUNE (north)
  { id: 'FO01', angle: 270, r: 1.8, links: ['ROOT'] },
  { id: 'FO02', angle: 262, r: 3.0, links: ['FO01'] },
  { id: 'FO07_RARITY', angle: 279, r: 3.0, links: ['FO01'] },
  { id: 'FO03', angle: 257, r: 4.2, links: ['FO02'] },
  { id: 'FO09', angle: 282, r: 4.2, links: ['FO07_RARITY'] },
  { id: 'FO04', angle: 256, r: 5.4, links: ['FO03'] },
  { id: 'FO08', angle: 283, r: 5.4, links: ['FO09'] },
  { id: 'FO05', angle: 262, r: 6.6, links: ['FO04'] },
  { id: 'FO06', angle: 278, r: 6.6, links: ['FO08'] },
  { id: 'FO10', angle: 270, r: 7.8, links: ['FO05', 'FO06'] },
  { id: 'FO12', angle: 270, r: 9.0, links: ['FO10'] },
  { id: 'FO11', angle: 258, r: 7.9, links: ['FO05'] },
  { id: 'FO13', angle: 270, r: 10.2, links: ['FO12'] },
  { id: 'FO14', angle: 270, r: 11.4, links: ['FO13'] },

  // GREED (north-east)
  { id: 'GR01', angle: 318, r: 1.8, links: ['ROOT', 'G_PAIR'] },
  { id: 'GR05', angle: 307, r: 3.0, links: ['GR01'] },
  { id: 'GR02', angle: 327, r: 3.0, links: ['GR01'] },
  { id: 'GR07', angle: 303, r: 4.2, links: ['GR05'] },
  { id: 'GR03', angle: 322, r: 4.2, links: ['GR02'] },
  { id: 'GR04', angle: 334, r: 5.4, links: ['GR03'] },
  { id: 'GR06', angle: 313, r: 5.4, links: ['GR03', 'GR07'] },
  { id: 'GR09', angle: 305, r: 6.6, links: ['GR06'] },
  { id: 'GR08', angle: 322, r: 6.6, links: ['GR06', 'GR04'] },
  { id: 'GR10', angle: 330, r: 7.8, links: ['GR08'] },
  { id: 'GR13', angle: 314, r: 7.8, links: ['GR08', 'GR09'] },
  { id: 'GR11', angle: 322, r: 9.0, links: ['GR13', 'GR10'] },
  { id: 'GR12', angle: 306, r: 9.0, links: ['GR13'] },
  { id: 'GR14', angle: 322, r: 10.2, links: ['GR11'] },

  // HANDS (south-east)
  { id: 'HA01', angle: 40, r: 3.0, links: ['G_HOLD'] },
  { id: 'HA05_NUDGE_SIGHT', angle: 34, r: 4.2, links: ['HA01'] },
  { id: 'HA02', angle: 52, r: 4.2, links: ['G_NUDGE', 'HA01'] },
  { id: 'HA03_MUSCLE_MEMORY', angle: 38, r: 5.4, links: ['HA05_NUDGE_SIGHT'] },
  { id: 'HA10', angle: 55, r: 5.4, links: ['HA02'] },
  { id: 'HA04', angle: 36, r: 6.6, links: ['HA03_MUSCLE_MEMORY'] },
  { id: 'HA08', angle: 52, r: 6.6, links: ['HA10'] },
  { id: 'HA07', angle: 44, r: 7.8, links: ['HA04', 'HA08'] },
  { id: 'HA09', angle: 38, r: 9.0, links: ['HA07'] },
  { id: 'HA06', angle: 50, r: 9.0, links: ['HA07'] },

  // CONTRACTS (north-west)
  { id: 'CO01', angle: 226, r: 1.8, links: ['ROOT'] },
  { id: 'CO02', angle: 216, r: 3.0, links: ['CO01'] },
  { id: 'CO04', angle: 236, r: 3.0, links: ['CO01'] },
  { id: 'CO03', angle: 212, r: 4.2, links: ['CO02'] },
  { id: 'CO14', angle: 240, r: 4.2, links: ['CO04'] },
  { id: 'CO07_ASCETIC', angle: 203, r: 5.4, links: ['CO03'] },
  { id: 'CO05', angle: 220, r: 5.4, links: ['CO03'] },
  { id: 'CO12', angle: 243, r: 5.4, links: ['CO14'] },
  { id: 'CO08', angle: 213, r: 6.6, links: ['CO05'] },
  { id: 'CO06', angle: 236, r: 6.6, links: ['CO12', 'CO05'] },
  { id: 'CO13', angle: 243, r: 7.8, links: ['CO06'] },
  { id: 'CO15', angle: 228, r: 7.8, links: ['CO06'] },
  { id: 'CO16', angle: 234, r: 9.0, links: ['CO15', 'CO13'] },
  { id: 'CO09', angle: 206, r: 7.8, links: ['CO08'] },
  { id: 'CO10', angle: 217, r: 9.0, links: ['CO09', 'CO15'] },
  { id: 'CO11', angle: 224, r: 10.2, links: ['CO10', 'CO16'] },
];

export function webPosition(p: WebPlacement): { x: number; y: number } {
  const a = (p.angle * Math.PI) / 180;
  return { x: Math.round(Math.cos(a) * p.r * WEB_UNIT), y: Math.round(Math.sin(a) * p.r * WEB_UNIT) };
}

/** Undirected adjacency built from the layout links. */
export function buildAdjacency(layout: WebPlacement[] = WEB_LAYOUT): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (!adj.has(a)) adj.set(a, new Set());
    adj.get(a)!.add(b);
  };
  for (const p of layout) {
    if (!adj.has(p.id)) adj.set(p.id, new Set());
    for (const l of p.links) {
      link(p.id, l);
      link(l, p.id);
    }
  }
  return adj;
}

/** Hops from ROOT for every node (the Web's depth). */
export function webDepth(adj: Map<string, Set<string>> = buildAdjacency()): Map<string, number> {
  const depth = new Map<string, number>([['ROOT', 0]]);
  const queue = ['ROOT'];
  while (queue.length) {
    const id = queue.shift()!;
    for (const n of adj.get(id) ?? []) {
      if (depth.has(n)) continue;
      depth.set(n, depth.get(id)! + 1);
      queue.push(n);
    }
  }
  return depth;
}

/** The route from ROOT to the Cathedral: the Web's spine, drawn brighter (docs/09 Phase 6). */
export function mainPath(target = 'G_GROW_5', adj: Map<string, Set<string>> = buildAdjacency()): string[] {
  const prev = new Map<string, string>();
  const seen = new Set(['ROOT']);
  const queue = ['ROOT'];
  while (queue.length) {
    const id = queue.shift()!;
    if (id === target) break;
    for (const n of adj.get(id) ?? []) {
      if (seen.has(n)) continue;
      seen.add(n);
      prev.set(n, id);
      queue.push(n);
    }
  }
  const path = [target];
  while (prev.has(path[0]!)) path.unshift(prev.get(path[0]!)!);
  return path[0] === 'ROOT' ? path : [];
}

/** What buying a node opens up: its neighbours one step further from ROOT. */
export function unlocksOf(id: string, adj: Map<string, Set<string>> = buildAdjacency(), depth = webDepth(adj)): string[] {
  const d = depth.get(id) ?? 0;
  return [...(adj.get(id) ?? [])].filter((n) => (depth.get(n) ?? 0) === d + 1);
}
